import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Shield, Plus, Edit2, Trash2, Check } from 'lucide-react';
import { paginateClientData } from '@/utils/pagination';

// Roles API
const rolesAPI = {
  list: () => api.get('/roles'),
  create: (data: any) => api.post('/roles', data),
  update: (id: number, data: any) => api.patch(`/roles/${id}`, data),
  delete: (id: number) => api.delete(`/roles/${id}`),
  permissions: () => api.get('/permissions'),
};

const PERMISSION_DETAILS: Record<string, { label: string; info: string }> = {
  manage_employees: {
    label: 'Управление сотрудниками',
    info: 'Создание, редактирование и ведение профилей сотрудников и назначений.',
  },
  manage_devices: {
    label: 'Управление устройствами',
    info: 'Регистрация устройств, обновление настроек и управление операциями.',
  },
  view_attendance: {
    label: 'Просмотр посещаемости',
    info: 'Просмотр событий посещаемости, табелей, опозданий и отсутствий.',
  },
  manage_penalties: {
    label: 'Управление штрафами',
    info: 'Создание штрафов и управление процессами, связанными со штрафами.',
  },
  approve_penalties: {
    label: 'Подтверждение штрафов',
    info: 'Подтверждение или отклонение штрафов перед финальной обработкой.',
  },
  view_payroll: {
    label: 'Просмотр зарплаты',
    info: 'Доступ к данным зарплаты, профилям начислений и результатам расчетов.',
  },
  manage_shifts: {
    label: 'Управление сменами',
    info: 'Создание смен, редактирование графиков и назначение рабочих смен.',
  },
  manage_branches: {
    label: 'Управление филиалами',
    info: 'Создание, редактирование и администрирование филиалов организации.',
  },
  manage_departments: {
    label: 'Управление отделами',
    info: 'Создание и редактирование отделов, привязанных к филиалам.',
  },
  manage_holidays: {
    label: 'Управление праздниками',
    info: 'Создание и редактирование праздничных и нерабочих дней.',
  },
  manage_payroll: {
    label: 'Управление зарплатой',
    info: 'Настройка зарплатных профилей, компонентов и правил расчета.',
  },
  run_payroll: {
    label: 'Запуск расчета зарплаты',
    info: 'Запуск, финализация и повторный пересчет зарплатных периодов.',
  },
  manage_users: {
    label: 'Управление пользователями',
    info: 'Создание, редактирование, деактивация и удаление пользователей CRM.',
  },
  manage_roles: {
    label: 'Управление ролями',
    info: 'Настройка ролей и их разрешений в системе.',
  },
  manage_leaves: {
    label: 'Управление отпусками',
    info: 'Создание и ведение заявок на отпуск и других видов отсутствий.',
  },
  approve_leaves: {
    label: 'Подтверждение отпусков',
    info: 'Одобрение и отклонение отпусков и связанных заявок.',
  },
};

const ROLE_GUIDES: Record<string, string> = {
  super_admin:
    'Полный доступ ко всем модулям: пользователи, роли, сотрудники, посещаемость, устройства, штрафы и зарплата.',
  hr_admin:
    'Отвечает за HR-процессы: сотрудники, контроль посещаемости, смены и управление штрафами.',
  accountant:
    'Фокус на зарплате и финансовом контроле: просмотр зарплаты, подтверждение штрафов и отчеты.',
  branch_manager:
    'Управляет посещаемостью и сменами на уровне филиала с ограниченными админ-правами.',
  viewer:
    'Доступ только на просмотр для мониторинга посещаемости и зарплаты без прав редактирования.',
};

/*
const MODULE_GUIDES_RU = [
  { title: 'Users', info: 'Управление учетными записями: создание, редактирование, деактивация пользователей.' },
  { title: 'Roles', info: 'Настройка ролей и прав доступа для сотрудников системы.' },
  { title: 'Employees', info: 'Кадровые данные сотрудников: профили, отделы, статусы и привязки.' },
  { title: 'Attendance', info: 'Контроль посещаемости: события, опоздания, отсутствия, табели.' },
  { title: 'Devices', info: 'Управление терминалами/устройствами и их синхронизацией.' },
  { title: 'Penalties', info: 'Правила и применение штрафов за нарушения дисциплины.' },
  { title: 'Payroll', info: 'Расчет заработной платы, профили выплат и связанные отчеты.' },
  { title: 'Shifts', info: 'Настройка смен, графиков и назначений сотрудников по времени.' },
  { title: 'Branches', info: 'Управление филиалами, их структурой и локальными настройками.' },
  { title: 'Reports', info: 'Просмотр и экспорт аналитических отчетов по работе системы.' },
];
*/

const getPermissionName = (permission: any) => {
  if (typeof permission === 'string') return permission;
  if (permission && typeof permission.name === 'string') return permission.name;
  return '';
};

const getPermissionLabel = (permissionName: string) =>
  PERMISSION_DETAILS[permissionName]?.label || permissionName;

const getPermissionInfo = (permissionName: string) =>
  PERMISSION_DETAILS[permissionName]?.info || 'Подробное описание пока не добавлено.';

export default function RolesPage() {
  const perPage = 6;
  const { can, refreshUser } = useAuth();
  const canManage = can('manage_roles');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesAPI.list(),
  });

  const { data: permissionsData } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => rolesAPI.permissions(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => rolesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      void refreshUser();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => rolesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      void refreshUser();
      setEditingRole(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rolesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      void refreshUser();
    },
  });

  const allRoles = Array.isArray(rolesData?.data)
    ? rolesData.data
    : rolesData?.data?.data || [];
  const { items: roles, meta } = paginateClientData<any>(allRoles, page, perPage);

  const availablePermissions = (Array.isArray(permissionsData?.data)
    ? permissionsData.data
    : permissionsData?.data?.data || []
  )
    .map((permission: any) => getPermissionName(permission))
    .filter(Boolean);

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту роль?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Роли и разрешения</h1>
        {canManage && (
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить роль
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm || !!editingRole}
        onClose={() => {
          setShowForm(false);
          setEditingRole(null);
        }}
        title={editingRole ? 'Редактировать роль' : 'Новая роль'}
      >
        <RoleForm
          initialData={editingRole}
          availablePermissions={availablePermissions}
          onSubmit={async (data) => {
            if (editingRole) {
              await updateMutation.mutateAsync({ id: editingRole.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingRole(null);
          }}
        />
      </Modal>

      {/* Roles List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roles.map((role: any) => (
          <Card key={role.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-purple-600" />
                  <div>
                    <h3 className="font-semibold">{role.name}</h3>
                    <p className="text-sm text-gray-500">{role.description || 'Без описания'}</p>
                  </div>
                </div>
                {role.is_system && (
                  <Badge variant="secondary">Системная</Badge>
                )}
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Разрешения:</p>
                <div className="flex flex-wrap gap-1">
                  {(role.permissions || [])
                    .map((perm: any) => getPermissionName(perm))
                    .filter(Boolean)
                    .slice(0, 6)
                    .map((permName: string) => (
                    <span
                      key={permName}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                    >
                      {getPermissionLabel(permName)}
                    </span>
                  ))}
                  {(role.permissions || []).length > 6 && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                      +{role.permissions.length - 6} еще
                    </span>
                  )}
                </div>
              </div>

              {canManage && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingRole(role)}
                    className="flex-1"
                    disabled={role.is_system}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Изменить
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(role.id)}
                    disabled={role.is_system}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Pagination meta={meta} onPageChange={setPage} />

      {roles.length === 0 && (
        <div className="text-center py-12">
          <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Роли отсутствуют</h3>
          <p className="text-gray-500">Создайте роль для начала работы.</p>
        </div>
      )}
    </div>
  );
}

function RoleForm({
  initialData,
  availablePermissions,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  availablePermissions: string[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    permissions: (initialData?.permissions || [])
      .map((perm: any) => getPermissionName(perm))
      .filter(Boolean),
  });

  const normalizedRoleName = formData.name.trim().toLowerCase();
  const selectedRoleGuide = ROLE_GUIDES[normalizedRoleName];
  const permissionsToRender =
    availablePermissions.length > 0
      ? availablePermissions
      : Object.keys(PERMISSION_DETAILS);

  const togglePermission = (key: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p: string) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          placeholder="Название роли (напр., hr_admin)"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          placeholder="Описание"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
      </div>

      <div className="rounded-lg border bg-blue-50 p-3 text-sm text-blue-900">
        <p className="font-medium mb-1">Обязанности роли</p>
        {selectedRoleGuide ? (
          <p>{selectedRoleGuide}</p>
        ) : (
          <p>
            Рекомендуемые названия ролей: <span className="font-medium">super_admin</span>,{' '}
            <span className="font-medium">hr_admin</span>,{' '}
            <span className="font-medium">accountant</span>,{' '}
            <span className="font-medium">branch_manager</span>,{' '}
            <span className="font-medium">viewer</span>.
          </p>
        )}
      </div>

      <div className="rounded-lg border bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-medium mb-2">Инфо по разрешениям</p>
        <div className="space-y-1">
          {permissionsToRender.map((permissionName) => (
            <p key={permissionName}>
              <span className="font-semibold">{getPermissionLabel(permissionName)}:</span>{' '}
              {getPermissionInfo(permissionName)}
            </p>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Разрешения</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {permissionsToRender.map((permissionName) => (
            <button
              key={permissionName}
              type="button"
              onClick={() => togglePermission(permissionName)}
              className={`flex items-center gap-2 p-2 rounded border text-left text-sm transition ${
                formData.permissions.includes(permissionName)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {formData.permissions.includes(permissionName) ? (
                <Check className="w-4 h-4 text-blue-600" />
              ) : (
                <div className="w-4 h-4 border rounded" />
              )}
              {getPermissionLabel(permissionName)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={() => onSubmit(formData)} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}


